import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { toast } from 'react-toastify';

import { fetchOrder } from 'entities/Order';
import { SelectedProposal } from 'entities/Proposal/model/types/ProposalSchema';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { EditItemsInOrderRequestData } from 'features/editItemsInOrder/model/types/editItemsInOrderSchema';

import { editOrderItemSourcingActions } from '../slices/EditOrderItemSourcingSlice';

import type { ThunkConfig } from 'app/providers/StoreProvider/config/types';

// Module-level abort controller. Only one instance of this feature can be active at a time. Don't stress.
let currentAbortController: AbortController | null = null;

/**
 * Saves the current sourcing configuration to the backend
 */
export const saveOrderItemSourcing = createAsyncThunk<
    void,
    void,
    ThunkConfig<string>
>('editOrderItemSourcing/saveOrderItemSourcing', async (_, thunkApi) => {
    const { rejectWithValue, getState, dispatch, extra } = thunkApi;
    const state = getState();

    const currentState = state.editOrderItemSourcing.currentState;
    const itemId = state.editOrderItemSourcing.itemId;
    const orderId = state.editOrderItemSourcing.orderId;

    if (!currentState || !itemId || !orderId) {
        return rejectWithValue('Missing required data for saving');
    }

    try {
        dispatch(editOrderItemSourcingActions.setLoading(true));

        const requestData: EditItemsInOrderRequestData = {
            quantity: currentState.totalQuantity,
        };

        // Only send selected proposals if sourcing is overridden, else wise let the API handle the sourcing
        if (currentState.isSourcingOverridden) {
            requestData.selectedProposals = currentState.selectedProposals.map(
                (proposal) => ({
                    uid: proposal.uid,
                    selectedQuantity: proposal.selectedQuantity || 0,
                }),
            );
        }

        await dispatch(
            editOrderItem({
                orderId,
                itemId,
                requestData,
            }),
        ).unwrap();

        // Refresh order data after successful save
        await dispatch(fetchOrder({ id: orderId })).unwrap();

        toast.success('Item updated successfully');
    } catch (error: unknown) {
        console.error('Save order item sourcing failed:', {
            error,
            itemId,
            orderId,
        });

        const errorMessage = getErrorMessage(error);
        if (errorMessage) {
            toast.error(errorMessage);
        }

        return rejectWithValue(errorMessage || 'An error occurred');
    } finally {
        dispatch(editOrderItemSourcingActions.setLoading(false));
    }
});

/**
 * Updates the total quantity and fetches new supplier souring distributions from the backend
 */
export const updateTotalQuantityWithSourcing = createAsyncThunk<
    void,
    { quantity: number },
    ThunkConfig<string>
>(
    'editOrderItemSourcing/updateTotalQuantityWithSourcing',
    async ({ quantity }, thunkApi) => {
        const { rejectWithValue, getState, dispatch, extra } = thunkApi;
        const state = getState();

        const itemId = state.editOrderItemSourcing.itemId;
        const orderId = state.editOrderItemSourcing.orderId;

        if (!itemId || !orderId) {
            return rejectWithValue('Missing required data for sourcing');
        }

        abortCurrentOperation();
        currentAbortController = new AbortController();

        try {
            dispatch(editOrderItemSourcingActions.setSourcingInProgress(true));

            const sourcingResponse = await callSourcingEngine(
                extra.api,
                orderId,
                itemId,
                quantity,
                currentAbortController.signal,
            );

            dispatch(
                editOrderItemSourcingActions.setTotalQuantityFromSourcing({
                    totalQuantity: sourcingResponse.totalQuantity,
                    selectedProposals: sourcingResponse.selectedProposals,
                }),
            );
        } catch (error: unknown) {
            console.error('Sourcing operation failed:', {
                error,
                quantity,
            });

            const errorMessage = getErrorMessage(error);
            if (!errorMessage) {
                return;
            }

            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        } finally {
            dispatch(editOrderItemSourcingActions.setSourcingInProgress(false));
            currentAbortController = null;
        }
    },
);

/**
 * Cancels any ongoing sourcing operation and resets the sourcing state
 */
export const cancelSourcingOperation = createAsyncThunk<
    void,
    void,
    ThunkConfig<string>
>('editOrderItemSourcing/cancelSourcingOperation', async (_, thunkApi) => {
    const { dispatch } = thunkApi;

    abortCurrentOperation();

    dispatch(editOrderItemSourcingActions.cancelSourcing());
});

/**
 * Calls the sourcing engine API to get supplier sourcings for a given quantity
 */
const callSourcingEngine = async (
    api: AxiosInstance,
    orderId: string,
    itemId: string,
    quantity: number,
    signal?: AbortSignal,
): Promise<{
    totalQuantity: number;
    selectedProposals: SelectedProposal[];
}> => {
    const response = await api.post(
        `/v1/orders/${orderId}/items/${itemId}`,
        { qty: quantity },
        { signal },
    );

    const proposals = response.data.data || [];

    return {
        totalQuantity: quantity,
        selectedProposals: proposals,
    };
};

const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
        if (error.name === 'AbortError') {
            return '';
        }

        if (
            'response' in error &&
            error.response &&
            typeof error.response === 'object' &&
            'status' in error.response
        ) {
            const status = (error.response as any).status;

            if (status === 404) {
                return 'Order not found. Please refresh the page.';
            } else if (status === 409) {
                return 'Order was modified by another user. Please refresh and try again.';
            } else if (status >= 500) {
                return 'Server error. Please try again later.';
            }
        }

        return error.message || 'An error occurred. Please try again.';
    }

    return 'An unexpected error occurred. Please try again.';
};

const abortCurrentOperation = (): void => {
    if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
    }
};
